import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { ChartSpline } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StorageKeys } from '~/atoms/StorageKeys'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { TranslationKeys } from '~/locales/i18next'
import {
  ExpandableTabChild,
  SiteNavigationExpandableTab,
} from '~/pages/Home/SiteNavigation/shared/SiteNavigationExpandableTab'

interface ReportingTabProps {
  isCollapsed?: boolean
  pathname: string
  callCenterEnabled?: boolean
  onNavigate: (path: string) => void
}

const reportingExpandedAtom = atomWithStorage<boolean>(
  StorageKeys.SiteNav + ':reporting-expanded',
  false,
)

export const ReportingTab = ({
  isCollapsed,
  pathname,
  callCenterEnabled,
  onNavigate,
}: ReportingTabProps) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useAtom(reportingExpandedAtom)

  const children = useMemo(() => {
    const items: ExpandableTabChild[] = []

    if (callCenterEnabled) {
      items.push(
        {
          id: 'queue-dashboard',
          label: t('dashboardsPage.item.queueDashboard' as TranslationKeys),
          path: `${ApplicationRoutes.REPORTING}/queue-dashboard`,
        },
        {
          id: 'agent-status',
          label: t('dashboardsPage.item.agentStatus' as TranslationKeys),
          path: `${ApplicationRoutes.REPORTING}/agent-status`,
        },
        {
          id: 'event-log',
          label: t('dashboardsPage.item.eventLog' as TranslationKeys),
          path: `${ApplicationRoutes.REPORTING}/event-log`,
        },
      )
    }

    items.push(
      {
        id: 'inbound-answer-rate',
        label: t('dashboardsPage.item.inboundAnswerRate' as TranslationKeys),
        path: `${ApplicationRoutes.REPORTING}/inbound-answer-rate`,
      },
      {
        id: 'number-comparison',
        label: t('dashboardsPage.item.numberComparison' as TranslationKeys),
        path: `${ApplicationRoutes.REPORTING}/number-comparison`,
      },
      {
        id: 'extension-comparison',
        label: t('dashboardsPage.item.extensionComparison' as TranslationKeys),
        path: `${ApplicationRoutes.REPORTING}/extension-comparison`,
      },
    )

    return items
  }, [callCenterEnabled, t])

  const isAnyChildActive = pathname.startsWith(ApplicationRoutes.REPORTING)

  const handleChildClick = (path: string) => {
    onNavigate(path)
  }

  return (
    <SiteNavigationExpandableTab
      id={ApplicationRoutes.REPORTING}
      title={t('nav.reporting' as TranslationKeys)}
      icon={ChartSpline}
      isCollapsed={isCollapsed}
      isExpanded={isExpanded}
      isAnyChildActive={isAnyChildActive}
      children={children}
      activeChildPath={pathname}
      onToggleExpand={() => setIsExpanded(!isExpanded)}
      onChildClick={handleChildClick}
    />
  )
}
